/**
 * TelegramConnectButton Component
 *
 * Connection button with code display dialog for linking Telegram accounts.
 * Shows connection status and allows connecting/disconnecting Telegram.
 * When connecting, displays a 6-character code for user to send to bot.
 */

import { memo, useReducer, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { ITelegramConnectButtonProps } from '@/@types/automation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MessageCircle, Check, X, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

type ConnectionState = {
  isDialogOpen: boolean;
  code: string | null;
  expiresAt: number | null;
  botUsername: string;
  timeRemaining: number;
  isStartingConnection: boolean;
  isDisconnecting: boolean;
};

type ConnectionAction =
  | { type: 'START_CONNECTING' }
  | {
      type: 'CONNECTION_STARTED';
      payload: { code: string; expiresAt: number; botUsername: string };
    }
  | { type: 'CONNECTION_FAILED' }
  | { type: 'START_DISCONNECTING' }
  | { type: 'DISCONNECT_COMPLETE' }
  | { type: 'DISCONNECT_FAILED' }
  | { type: 'CLOSE_DIALOG' }
  | { type: 'SET_TIME_REMAINING'; payload: number };

const initialConnectionState: ConnectionState = {
  isDialogOpen: false,
  code: null,
  expiresAt: null,
  botUsername: '',
  timeRemaining: 0,
  isStartingConnection: false,
  isDisconnecting: false,
};

function connectionReducer(state: ConnectionState, action: ConnectionAction): ConnectionState {
  switch (action.type) {
    case 'START_CONNECTING':
      return { ...state, isStartingConnection: true };
    case 'CONNECTION_STARTED':
      return {
        ...state,
        isStartingConnection: false,
        isDialogOpen: true,
        code: action.payload.code,
        expiresAt: action.payload.expiresAt,
        botUsername: action.payload.botUsername,
      };
    case 'CONNECTION_FAILED':
      return { ...state, isStartingConnection: false };
    case 'START_DISCONNECTING':
      return { ...state, isDisconnecting: true };
    case 'DISCONNECT_COMPLETE':
      return { ...state, isDisconnecting: false };
    case 'DISCONNECT_FAILED':
      return { ...state, isDisconnecting: false };
    case 'CLOSE_DIALOG':
      return {
        ...state,
        isDialogOpen: false,
        code: null,
        expiresAt: null,
        botUsername: '',
        timeRemaining: 0,
      };
    case 'SET_TIME_REMAINING':
      return { ...state, timeRemaining: action.payload };
  }
}

export const TelegramConnectButton = memo(function TelegramConnectButton({
  connection: externalConnection,
  isLoading: externalLoading,
  onDisconnect,
  className,
}: ITelegramConnectButtonProps) {
  const [connState, dispatch] = useReducer(connectionReducer, initialConnectionState);
  const {
    isDialogOpen,
    code: dialogCode,
    expiresAt: dialogExpiresAt,
    botUsername: dialogBotUsername,
    timeRemaining,
    isStartingConnection,
    isDisconnecting,
  } = connState;

  // Fetch current connection status (use external if provided)
  const queryConnection = useQuery(api.telegram.connection.get);
  const connection = externalConnection !== undefined ? externalConnection : queryConnection;
  const isLoading = externalLoading !== undefined ? externalLoading : connection === undefined;
  const isConnected = connection?.status === 'active';

  // Mutations
  const startConnectionMutation = useMutation(api.telegram.connection.startConnection);
  const disconnectMutation = useMutation(api.telegram.connection.disconnect);

  // Update time remaining countdown
  useEffect(() => {
    if (!dialogExpiresAt) {
      dispatch({ type: 'SET_TIME_REMAINING', payload: 0 });
      return;
    }

    const updateTimeRemaining = () => {
      const remaining = Math.max(0, Math.floor((dialogExpiresAt - Date.now()) / 1000));
      dispatch({ type: 'SET_TIME_REMAINING', payload: remaining });
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [dialogExpiresAt]);

  // Handle connect click
  const handleConnectClick = useCallback(async () => {
    dispatch({ type: 'START_CONNECTING' });
    try {
      const result = await startConnectionMutation();
      dispatch({
        type: 'CONNECTION_STARTED',
        payload: {
          code: result.code,
          expiresAt: result.expiresAt,
          botUsername: result.botUsername,
        },
      });
    } catch (error) {
      toast.error('Failed to start connection');
      console.error(error);
      dispatch({ type: 'CONNECTION_FAILED' });
    }
  }, [startConnectionMutation]);

  // Handle disconnect
  const handleDisconnect = useCallback(async () => {
    dispatch({ type: 'START_DISCONNECTING' });
    try {
      await disconnectMutation();
      toast.success('Telegram disconnected');
      dispatch({ type: 'DISCONNECT_COMPLETE' });
      onDisconnect?.();
    } catch (error) {
      toast.error('Failed to disconnect');
      console.error(error);
      dispatch({ type: 'DISCONNECT_FAILED' });
    }
  }, [disconnectMutation, onDisconnect]);

  // Copy code to clipboard
  const handleCopyCode = useCallback(() => {
    if (dialogCode) {
      navigator.clipboard.writeText(dialogCode);
      toast.success('Code copied to clipboard');
    }
  }, [dialogCode]);

  // Open Telegram bot
  const handleOpenBot = useCallback(() => {
    if (dialogBotUsername) {
      window.open(`https://t.me/${dialogBotUsername}`, '_blank');
    }
  }, [dialogBotUsername]);

  // Close dialog and reset
  const handleCloseDialog = useCallback(() => {
    dispatch({ type: 'CLOSE_DIALOG' });
  }, []);

  // Close dialog when connection is established
  useEffect(() => {
    if (isConnected && isDialogOpen) {
      toast.success('Telegram connected successfully!');
      dispatch({ type: 'CLOSE_DIALOG' });
    }
  }, [isConnected, isDialogOpen]);

  // Calculate formatted time remaining
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  // Loading state
  if (isLoading) {
    return (
      <Button variant="outline" disabled className={className}>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  // Connected state
  if (isConnected && connection) {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <div className="flex items-center gap-2 text-sm">
          <Check className="h-4 w-4 text-green-500" />
          <span>Connected as </span>
          <span className="font-medium">
            @{connection.telegramUsername || connection.telegramFirstName || 'Telegram User'}
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={isDisconnecting}>
          {isDisconnecting ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <X className="h-4 w-4 mr-1" />
          )}
          Disconnect
        </Button>
      </div>
    );
  }

  // Not connected state
  return (
    <>
      <Button
        variant="outline"
        onClick={handleConnectClick}
        disabled={isStartingConnection}
        className={className}
      >
        {isStartingConnection ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <MessageCircle className="h-4 w-4 mr-2" />
        )}
        Connect Telegram
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Connect Telegram
            </DialogTitle>
            <DialogDescription>
              Send the code below to our Telegram bot to connect your account
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Connection Code */}
            <div className="space-y-2">
              <label htmlFor="telegram-connection-code" className="text-sm font-medium">
                Your Connection Code
              </label>
              <div className="flex gap-2">
                <Input
                  id="telegram-connection-code"
                  value={dialogCode || ''}
                  readOnly
                  className="font-mono text-2xl text-center tracking-widest"
                />
                <Button variant="outline" size="icon" onClick={handleCopyCode}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {timeRemaining > 0 && (
                <p className="text-sm text-muted-foreground">
                  Expires in {minutes}:{seconds.toString().padStart(2, '0')}
                </p>
              )}
              {timeRemaining === 0 && dialogCode && (
                <p className="text-sm text-destructive">
                  Code expired. Please close and try again.
                </p>
              )}
            </div>

            {/* Instructions */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Instructions:</p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Open our Telegram bot</li>
                <li>Send the code above to the bot</li>
                <li>Wait for confirmation</li>
              </ol>
            </div>

            {/* Open Bot Button */}
            <Button variant="default" className="w-full" onClick={handleOpenBot}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Open @{dialogBotUsername} in Telegram
            </Button>

            {/* Waiting indicator */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Waiting for connection...
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});
